/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { ChoiceGroup } from '@fluentui/react/lib/ChoiceGroup';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { CustomTextProjectKind } from 'src/customText/core/stores/CustomTextProjectStore';
import { DatasetType } from 'src/modules/modelTraining/types/trainingTypes';

type CustomTextChooseDatasetProps = {
    tooltipMessage?: string;
    isSelectionDisabled?: boolean;
    currentSelection: DatasetType;
    viewingOption: 'single' | 'all';
    projectKind: CustomTextProjectKind;
    onChangeSelection: (dataset: DatasetType) => void;
};

const messages = defineMessages({
    title: { id: 'customText.customTextChooseDataset.title', defaultMessage: 'Assign data to training or test set' },
    subTitle: { id: 'customText.customTextChooseDataset.subTitle', defaultMessage: 'Add this document to the dataset for' },
    description: {
        id: 'customText.customTextChooseDataset.description',
        defaultMessage:
            'Split this project’s documents into separate sets to train your model and test its performance. The model will use the training set to learn from your labeled data, and we’ll use the test set to calculate evaluation metrics. To use automatic data splitting later, leave all documents set to the default option here.'
    },
    testSet: { id: 'customText.customTextChooseDataset.testSet', defaultMessage: 'Testing the model’s performance' },
    trainSet: { id: 'customText.customTextChooseDataset.trainSet', defaultMessage: 'Training the custom model (default)' },
    disabledTooltipMsg: { id: 'customText.customTextChooseDataset.disabledTooltipMsg', defaultMessage: 'Please select document(s).' },
    dataSplitLearnMore: { id: 'customText.customTextChooseDataset.dataSplitLearnMore', defaultMessage: 'Learn more about data splitting' },
    connectedToAml: {
        id: 'customText.customTextChooseDataset.connectedToAml',
        defaultMessage: 'This project is connected to Azure Machine Learning, this feature is disabled.'
    }
});

const { styleComponent } = getStylistV2('CustomTextChooseDataset');
const Container = styleComponent<IStackProps>(Stack)('Container', theme => ({
    borderTop: `1px solid ${theme.borderColor}`,
    margin: '0px 16px',
    padding: '21px 0px'
}));

export const CustomTextChooseDataset = observer((props: CustomTextChooseDatasetProps) => {
    const { projectKind, tooltipMessage, currentSelection, isSelectionDisabled = false, onChangeSelection, viewingOption } = props;

    const { rootStore } = useGlobalState();
    const { amlProjectPath } = rootStore.currentCustomTextProjectStore;
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    React.useEffect(() => {
        if (!currentSelection && viewingOption === 'single' && !isSelectionDisabled) {
            onChangeSelection('Train');
        }
    }, [currentSelection, viewingOption]);

    return (
        <Container>
            <Stack tokens={{ childrenGap: 15 }}>
                <Stack horizontal verticalAlign="center">
                    <LuisText fontWeight={600}>{localize(messages.title)}</LuisText>
                    <CalloutButton content={localize(messages.description)} />
                </Stack>
                <Stack>
                    <LuisText>{localize(messages.subTitle)}</LuisText>

                    {canWrite ? (
                        <LuisTooltip
                            directionalHint={DirectionalHint.rightCenter}
                            content={
                                (!!amlProjectPath && localize(messages.connectedToAml)) ||
                                (isSelectionDisabled && (tooltipMessage || localize(messages.disabledTooltipMsg)))
                            }>
                            <ChoiceGroup
                                disabled={!!amlProjectPath || isSelectionDisabled}
                                selectedKey={!isSelectionDisabled && currentSelection}
                                onChange={(_, option) => {
                                    onChangeSelection(option.key as DatasetType);
                                    logButtonClick(
                                        serviceName,
                                        'DataLabeling',
                                        `Assign${option.key}Set`,
                                        null,
                                        rootStore.currentCustomTextProjectStore?.id
                                    );
                                }}
                                options={[
                                    {
                                        key: 'Train',
                                        text: localize(messages.trainSet),
                                        ariaLabel: `${localize(messages.subTitle)} ${localize(messages.trainSet)}`
                                    },
                                    {
                                        key: 'Test',
                                        text: localize(messages.testSet),
                                        ariaLabel: `${localize(messages.subTitle)} ${localize(messages.testSet)}`
                                    }
                                ]}
                            />
                        </LuisTooltip>
                    ) : (
                        <RBACTooltip
                            hidden={isSelectionDisabled}
                            displayedUserRole={displayedUserRole}
                            data-automation-id="selectDatasetLanguageToolTip"
                            componentSurroundedByToolTip={
                                <ChoiceGroup
                                    disabled
                                    selectedKey={!isSelectionDisabled && currentSelection}
                                    onChange={(_, option) => onChangeSelection(option.key as DatasetType)}
                                    options={[
                                        { key: 'Train', text: localize(messages.trainSet) },
                                        { key: 'Test', text: localize(messages.testSet) }
                                    ]}
                                />
                            }
                        />
                    )}

                    <ExternalLink
                        href={`https://aka.ms/${
                            projectKind === 'CustomEntityRecognition' ? 'ct-training-concepts' : 'ctc-training-concepts'
                        }`}>
                        {localize(messages.dataSplitLearnMore)}
                    </ExternalLink>
                </Stack>
            </Stack>
        </Container>
    );
});
